import { useState } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import classNames from 'classnames';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';

import * as K from 'common/constants';
import ELEMENT, { createTestId } from 'common/element';
import { UrlParams } from 'common/types';
import { addTermToFilter, buildParamsWithDeconstructedFilter } from 'common/util/filter/filter';
import { getLinkForRoute } from 'common/util/location';

import Link from 'components/common/Link/Link';
import {
  draftSearchParamState,
  eventParamsState,
  routeState,
} from 'components/common/Managers/RouteStateManager.state';
import { viewState } from 'components/common/Managers/ViewStateManager.state';

import './FacetFilter.scss';

type FacetFilterProps = {
  field?: string;
  value?: string | number | boolean;
  onSearch?: () => void;
  onCopy?: () => void;
  valueIsNumeric?: boolean;
  showDisabled?: boolean;
  index?: number;
};

/**
 * This component provides a list of comparators for a given field and value. For example,
 * if we have a field 'bytes' with a value of 5000, the user would see < == != > and clicking
 * on one of those runs the corresponding search. If the value is not numeric, the < and >
 * comparators will either be disabled or omitted.
 *
 * @prop field             facet name
 * @prop value             facet value (strings assumed to be quoted)
 * @prop onSearch          function to run after issuing a search
 * @prop onCopy            function to run when user clicks copy button
 * @prop valueIsNumeric    if true, the value is numeric and we will show < and >
 * @prop showDisabled      if true, show disabled comparators
 * @prop index             index of facet value
 */
function FacetFilter({
  field,
  value,
  onSearch,
  onCopy,
  valueIsNumeric = false,
  showDisabled = false,
  index,
}: FacetFilterProps): JSX.Element {
  const eventParams = useRecoilValue(eventParamsState);
  const route = useRecoilValue(routeState);
  const [curLink, setCurLink] = useState('');

  // update params using draft params, adding in a new term
  const getLink = useRecoilCallback(({ snapshot }) => (op: string): string => {
    const draftParams = snapshot.getLoadable(draftSearchParamState).contents as UrlParams;
    const newFilter = addTermToFilter({
      filter: draftParams[K.URL_PARAM.FILTER] || '',
      field: field || '',
      op,
      value: value != null ? String(value) : '',
    });
    const params = { ...draftParams, [K.URL_PARAM.FILTER]: newFilter } as UrlParams;
    const view = snapshot.getLoadable(viewState).getValue();
    const adjustedParams = buildParamsWithDeconstructedFilter(params, view);
    return getLinkForRoute(route, eventParams, adjustedParams);
  });

  // create a link based on an operator
  const getLinkComponent = (op: string, text: string, className: string, testId: string): JSX.Element => (
    <Link
      className={className}
      href={curLink}
      onHover={(): void => setCurLink(getLink(op))}
      testId={index != null ? createTestId(testId, index) : undefined}
      onClick={onSearch ? (): void => onSearch() : undefined}
    >
      {text}
    </Link>
  );

  let lessThanLink = null;
  let greaterThanLink = null;
  if (valueIsNumeric || showDisabled) {
    const className = classNames('FacetFilter', { 'FacetFilter--Disabled': !valueIsNumeric });
    lessThanLink = getLinkComponent('<', '<', className, ELEMENT.facetValueLess);
    greaterThanLink = getLinkComponent('>', '>', className, ELEMENT.facetValueGreater);
  }
  const equalsLink = getLinkComponent('=', '=', 'FacetFilter FacetFilter--Equal', ELEMENT.facetValueEqual);
  const notEqualsLink = getLinkComponent('!=', '!=', 'FacetFilter', ELEMENT.facetValueNotEqual);

  let copyButton = null;
  if (onCopy) {
    copyButton = (
      <span className="FacetFilter" onClick={(): void => onCopy()} role="button" tabIndex={0}>
        <FileCopyOutlinedIcon fontSize="small" className="FacetFilter__Copy" />
      </span>
    );
  }

  return (
    <>
      {lessThanLink}
      {equalsLink}
      {notEqualsLink}
      {greaterThanLink}
      {copyButton}
    </>
  );
}

export default FacetFilter;
